import React from 'react';
import {Page, Text, Image, View, Document, StyleSheet, Font} from '@react-pdf/renderer';
// Pdfkit does not seem to support webp images: https://github.com/diegomura/react-pdf/pull/2463#issuecomment-1891121379
// Make sure this is a real JPG!!!!
import logo from "../logo-big.jpg";
import font from "../fonts/old-fonts/Montserrat-Regular.ttf"

Font.register({
    family: "Montserrat",
    format: "truetype",
    src: font
});

// Disable mid-word hyphenation so text only wraps between words, not within them.
Font.registerHyphenationCallback(word => [word]);

const brandColor = process.env.REACT_APP_BRAND_COLOR ?? '#333333';

// Create styles
const styles = StyleSheet.create({
    page: {
        backgroundColor: '#FFFFFF',
        fontFamily: 'Montserrat',
        fontSize: 10,
        padding: 40,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 30,
    },
    logoContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
    },
    logo: {
        width: 100,
        height: 'auto',
    },
    titleContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
    },
    title: {
        fontSize: 32,
        fontWeight: 'bold',
        color: brandColor,
    },
    fromToSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    fromSection: {
        width: '48%',
    },
    toSection: {
        width: '48%',
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 5,
        color: brandColor,
    },
    text: {
        fontSize: 9,
        marginBottom: 2,
    },
    textCompanyName: {
      fontSize: 18,
      fontWeight: 'bold',
      color: brandColor,
      marginBottom: 10,
    },
    invoiceDetailsSection: {
        backgroundColor: '#f5f5f5',
        padding: 15,
        marginBottom: 10,
        borderRadius: 4,
    },
    invoiceDetailsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
    },
    invoiceLabel: {
        fontSize: 10,
        fontWeight: 'bold',
        width: '50%',
    },
    invoiceValue: {
        fontSize: 10,
        width: '50%',
        textAlign: 'right',
    },
    table: {
        marginTop: 10,
        marginBottom: 10,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: brandColor,
        color: '#FFFFFF',
        padding: 8,
        fontWeight: 'bold',
        fontSize: 10,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#eeeeee',
        padding: 8,
        fontSize: 9,
    },
    tableColBottleQty: {
        width: '15%',
        textAlign: 'center',
    },
    tableColDescription: {
        width: '45%',
    },
    tableColNett: {
        width: '20%',
        textAlign: 'right',
    },
    tableColVAT: {
        width: '20%',
        textAlign: 'right',
    },
    totalsSection: {
        marginTop: 20,
        alignItems: 'flex-end',
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 200,
        marginBottom: 5,
    },
    totalLabel: {
        fontSize: 10,
        fontWeight: 'bold',
    },
    totalValue: {
        fontSize: 10,
    },
    grandTotalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 200,
        marginTop: 5,
        paddingTop: 5,
        borderTopWidth: 2,
        borderTopColor: brandColor,
    },
    grandTotalLabel: {
        fontSize: 12,
        fontWeight: 'bold',
        color: brandColor,
    },
    grandTotalValue: {
        fontSize: 12,
        fontWeight: 'bold',
        color: brandColor,
    },
    footer: {
        position: 'absolute',
        bottom: 40,
        left: 40,
        right: 40,
        fontSize: 8,
        color: '#666666',
        textAlign: 'center',
        borderTopWidth: 1,
        borderTopColor: '#eeeeee',
        paddingTop: 10,
    },
});

// Must return a real boolean: in `{cond && <Text/>}` a falsy non-boolean (e.g. 0)
// would be rendered as a bare node inside a View, which react-pdf rejects with
// "Type mismatch".
const isValidString = (str) => {
    return typeof str === 'string' && str.trim().length > 0;
};

// Only render values react-pdf can put inside <Text>.
const toText = (value) => {
    return typeof value === 'string' || typeof value === 'number' ? value : '';
};

const normalizeNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value
      .trim();

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

// Create Invoice Document Component
const Invoice = (props) => {
    const {order, orderArray, totalNett, totalVAT, grandTotal, currency} = props;

    const safeOrderArray = orderArray || [];
    const safeCurrency = currency || '£';

    return (
        <Document title={`Invoice ${order.Order_InvoiceNumber || ''}`}>
            <Page size="A4" style={styles.page}>
                {/* Header with Logo and Title */}
                <View style={styles.header}>
                  <View style={styles.logoContainer}>
                    <Image style={styles.logo} src={logo} />
                  </View>
                  <View style={styles.titleContainer}>
                    <Text style={styles.title}>INVOICE</Text>
                  </View>
                </View>

                {/* From and To Section */}
                <View style={styles.fromToSection}>
                    {/* From Section */}
                    <View style={styles.fromSection}>
                        {isValidString(process.env.REACT_APP_COMPANY_NAME) && <Text style={styles.textCompanyName}>{process.env.REACT_APP_COMPANY_NAME}</Text>}
                        {isValidString(process.env.REACT_APP_ADDRESS) && <Text style={styles.text}>{process.env.REACT_APP_ADDRESS}</Text>}
                        {isValidString(process.env.REACT_APP_PHONE) && <Text style={styles.text}>{process.env.REACT_APP_PHONE}</Text>}
                        {isValidString(process.env.REACT_APP_EMAIL) && <Text style={styles.text}>{process.env.REACT_APP_EMAIL}</Text>}
                        {isValidString(process.env.REACT_APP_INVOICE_VAT_NUMBER) && <Text style={styles.text}>VAT number: {process.env.REACT_APP_INVOICE_VAT_NUMBER}</Text>}
                    </View>

                    {/* Invoice Details */}
                    <View style={styles.toSection}>
                        <View style={styles.invoiceDetailsSection}>
                            {!!toText(order.Order_InvoiceNumber) && (
                                <View style={styles.invoiceDetailsRow}>
                                    <Text style={styles.invoiceLabel}>Invoice Number:</Text>
                                    <Text style={styles.invoiceValue}>{toText(order.Order_InvoiceNumber)}</Text>
                                </View>
                            )}
                            <View style={styles.invoiceDetailsRow}>
                                <Text style={styles.invoiceLabel}>Order Number:</Text>
                                <Text style={styles.invoiceValue}>{toText(order.Order_OrderNo)}</Text>
                            </View>
                            <View style={styles.invoiceDetailsRow}>
                                <Text style={styles.invoiceLabel}>Order Date:</Text>
                                <Text style={styles.invoiceValue}>
                                    {new Date(order.Order_OrderDate).toLocaleDateString()}
                                </Text>
                            </View>
                            <View style={styles.invoiceDetailsRow}>
                                <Text style={styles.invoiceLabel}>Delivery Date:</Text>
                                <Text style={styles.invoiceValue}>
                                    {new Date(order.RequiredDate).toLocaleDateString()}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                 {/*Delivery and Billing Addresses */}
                <View style={styles.fromToSection}>
                    {/* Delivery Address */}
                    <View style={styles.fromSection}>
                        <Text style={styles.sectionTitle}>Delivery Address:</Text>
                        {isValidString(order.Delivery_Name) && <Text style={styles.text}>{order.Delivery_Name}</Text>}
                        {isValidString(order.Delivery_Address1) && <Text style={styles.text}>{order.Delivery_Address1}</Text>}
                        {isValidString(order.Delivery_Address2) && <Text style={styles.text}>{order.Delivery_Address2}</Text>}
                        {isValidString(order.Delivery_Address3) && <Text style={styles.text}>{order.Delivery_Address3}</Text>}
                        {isValidString(order.Delivery_Address4) && <Text style={styles.text}>{order.Delivery_Address4}</Text>}
                        {isValidString(order.Delivery_Address5) && <Text style={styles.text}>{order.Delivery_Address5}</Text>}
                    </View>

                     {/*Billing Address */}
                    <View style={styles.toSection}>
                        <Text style={styles.sectionTitle}>Billing Address:</Text>
                        {isValidString(order.Billing_Name) && <Text style={styles.text}>{order.Billing_Name}</Text>}
                        {isValidString(order.Billing_Address1) && <Text style={styles.text}>{order.Billing_Address1}</Text>}
                        {isValidString(order.Billing_Address2) && <Text style={styles.text}>{order.Billing_Address2}</Text>}
                        {isValidString(order.Billing_Address3) && <Text style={styles.text}>{order.Billing_Address3}</Text>}
                        {isValidString(order.Billing_Address4) && <Text style={styles.text}>{order.Billing_Address4}</Text>}
                        {isValidString(order.Billing_Address5) && <Text style={styles.text}>{order.Billing_Address5}</Text>}
                    </View>
                </View>

                {/* Customer Reference */}
                {isValidString(order.Order_CustomerReference) && (
                    <View style={{marginBottom: 5}}>
                        <Text style={styles.text}>
                            Customer Ref: {order.Order_CustomerReference}
                        </Text>
                    </View>
                )}

                {/* Products Table */}
                <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.tableHeader}>
                        <Text style={styles.tableColBottleQty}>Bottle Qty</Text>
                        <Text style={styles.tableColDescription}>Product</Text>
                        <Text style={styles.tableColNett}>Nett</Text>
                        <Text style={styles.tableColVAT}>VAT</Text>
                    </View>

                    {/* Table Rows */}
                    {safeOrderArray.map((item, index) => (
                        <View style={styles.tableRow} key={index}>
                            <Text style={styles.tableColBottleQty}>{normalizeNumber(item.TotalBottles)}</Text>
                            <Text style={styles.tableColDescription}>{toText(item.Description)}</Text>
                            <Text style={styles.tableColNett}>
                                {safeCurrency}{normalizeNumber(item.LineNett).toFixed(2)}
                            </Text>
                            <Text style={styles.tableColVAT}>
                                {safeCurrency}{normalizeNumber(item.LineVAT).toFixed(2)}
                            </Text>
                        </View>
                    ))}
                </View>

                 {/* Totals Section */}
                <View style={styles.totalsSection}>
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Total Nett:</Text>
                        <Text style={styles.totalValue}>
                            {safeCurrency}{normalizeNumber(totalNett).toFixed(2)}
                        </Text>
                    </View>
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Total VAT (20%):</Text>
                        <Text style={styles.totalValue}>
                            {safeCurrency}{normalizeNumber(totalVAT).toFixed(2)}
                        </Text>
                    </View>
                    <View style={styles.grandTotalRow}>
                        <Text style={styles.grandTotalLabel}>Grand Total:</Text>
                        <Text style={styles.grandTotalValue}>
                            {safeCurrency}{normalizeNumber(grandTotal).toFixed(2)}
                        </Text>
                    </View>
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                    <Text>© {new Date().getFullYear()} {process.env.REACT_APP_COMPANY_NAME ?? ''} - All rights reserved</Text>
                </View>
            </Page>
        </Document>
    );
};

export default Invoice;